import { useState } from "react";
import { ShieldCheck, Shield, Heart, Lock, Users, CheckCircle2, ArrowRight, ChevronDown, ExternalLink, UserPlus, FileCheck, Sparkles, MessagesSquare, Menu } from "lucide-react";
import { Button } from "../components/ui/button";
import AuroraFooter from "./public/AuroraFooter";
import AuroraSlideMenu from "./public/AuroraSlideMenu";
import ChevronSteps from "./public/ChevronSteps";
import TaglineBar from "./public/TaglineBar";
import "./LandingHero.css";
import { StoryCards } from "./public/PlaceholderPage";
import { publicPages } from "../routes/publicPages";

const SUCCESS_STORIES = publicPages.find((p) => p.path === "/success-stories")?.stories || [];

const HERO_IMG = "/hero-1400.jpg";
// Hero sizing + chevron position (desktop and mobile) live in LandingHero.css;
// tune --hero-chevron-top there to move the bar relative to the shoreline.
// Shared width for ChevronSteps + TaglineBar (desktop), centred so open water
// shows on both sides.
const HERO_OVERLAY_WIDTH = "65%";
// Matches the deep water at the bottom edge of the photo, shown only if the
// overlay ever runs taller than the image.
const HERO_FILL = "#062331";

const COUPLE_IMG = "/aurora/middle-couple.jpg";
const BENCH_IMG = "/aurora/bottom-couple.jpg";

const features = [
  {
    icon: Shield,
    title: "FBI Background Checks",
    desc: "Every user undergoes a thorough FBI Identity History Summary Check before accessing the platform.",
  },
  {
    icon: CheckCircle2,
    title: "What You See Is What You Get",
    desc: "All profiles are verified. No catfishing, no fake photos, no surprises. Real people, real connections.",
  },
  {
    icon: Lock,
    title: "End-to-End Security",
    desc: "Your data is protected with enterprise-grade encryption. We take your privacy as seriously as your heart.",
  },
  {
    icon: Heart,
    title: "Meaningful Matches",
    desc: "Our matching system connects you with compatible, verified individuals who are serious about dating.",
  },
];

const stats = [
  { value: "100%", label: "Verified Users" },
  { value: "0", label: "Catfish Reports" },
  { value: "50K+", label: "Safe Matches" },
  { value: "FBI", label: "Grade Security" },
];

const steps = [
  { num: "01", title: "Create Your Account", desc: "Sign up with your email and basic information." },
  { num: "02", title: "Get FBI Verified", desc: "Submit your FBI Identity History Summary Check reference number." },
  { num: "03", title: "Admin Approval", desc: "Our security team reviews and approves your verification." },
  { num: "04", title: "Start Dating Safely", desc: "Browse verified profiles and make genuine connections." },
];

export default function LandingPage({ onGetStarted, onSignIn }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-[#FFF9F5]" data-testid="landing-page">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 px-3 sm:px-4 md:px-6 py-2.5 flex items-center gap-2 sm:gap-3 bg-[#FFF9F5]/95 backdrop-blur-xl border-b border-[#F0E6DD]">
        <button
          data-testid="landing-hamburger-btn"
          onClick={() => setMenuOpen(true)}
          className="text-[#1A1A1A] hover:text-[#FF3B6B] transition-colors p-1.5 rounded-md hover:bg-black/5 shrink-0"
          aria-label="Open menu"
        >
          <Menu size={20} strokeWidth={1.8} />
        </button>
        <img
          src="/aurora-logo.png"
          alt="Aurora"
          className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl object-cover shadow-sm shrink-0"
          data-testid="aurora-brand-logo"
        />
        <div
          data-testid="landing-trust-banner"
          className="flex-1 min-w-0 mx-1 px-3 py-1.5 sm:py-2 rounded-lg bg-[#1A1A1A] flex items-center justify-center text-center shadow-sm"
        >
          <p
            className="text-white text-[10px] sm:text-xs leading-tight font-semibold"
            style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
          >
            Where trust lead to real love.
            <span className="block font-normal opacity-95">trusted by thousands.</span>
          </p>
        </div>
        <button
          data-testid="landing-signin-btn"
          onClick={onSignIn}
          className="shrink-0 text-[#1A1A1A] text-sm font-medium hover:text-[#FF3B6B] transition-colors px-3 py-1.5 rounded-md hover:bg-black/5"
          style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
        >
          Sign In
        </button>
      </nav>

      <AuroraSlideMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Hero — background photo with the chevron steps + tagline layered on top.
          The sizer and the overlay share one grid cell, so the section is as tall
          as whichever is taller and the photo is never cropped or altered. */}
      <section
        data-testid="hero-section"
        role="img"
        aria-label="Aurora — a new beginning in life"
        className="landing-hero relative grid w-full overflow-hidden"
        style={{ backgroundColor: HERO_FILL, backgroundImage: `url(${HERO_IMG})` }}
      >
        <div aria-hidden="true" className="landing-hero__sizer [grid-area:1/1]" />
        <div className="[grid-area:1/1] flex flex-col">
          <ChevronSteps width={HERO_OVERLAY_WIDTH} marginTop="var(--hero-chevron-top)" />
          <TaglineBar width={HERO_OVERLAY_WIDTH} marginTop={0} />
        </div>
      </section>

      {/* Your Path to Safe Dating */}
      <section
        data-testid="path-to-safe-dating-section"
        className="relative bg-[#0B0B0F] text-white pt-6 pb-20 md:py-20 px-6 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[480px] h-[480px] bg-[#FF3B6B]/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-0 right-1/4 w-[480px] h-[480px] bg-[#22D3A4]/10 blur-[120px] rounded-full" />
        </div>

        <div className="relative max-w-3xl mx-auto">
          <ol className="space-y-5">
            <li>
              <button
                type="button"
                data-testid="path-step-1-register"
                onClick={onGetStarted}
                className="group w-full text-left rounded-2xl border-2 border-[#5B6CFF]/60 bg-[#5B6CFF]/5 hover:bg-[#5B6CFF]/10 hover:border-[#5B6CFF] transition-all p-5 sm:p-6 flex gap-4 items-start"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#5B6CFF] flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-[#5B6CFF]/40">
                  1
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-lg mb-1">STEP 1 — Register</div>
                  <p className="text-[#A8A8B3] text-sm leading-relaxed mb-3">
                    Start by creating your profile and agreeing to our terms.
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#5B6CFF]/60 text-[#8B9DFF] text-xs font-bold uppercase tracking-wider group-hover:bg-[#5B6CFF]/15 transition-colors">
                    <UserPlus className="w-3.5 h-3.5" />
                    Register / Sign Up
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </button>
            </li>

            <li className="flex justify-center" aria-hidden="true">
              <ChevronDown className="w-6 h-6 text-[#5B6CFF]" />
            </li>

            <li>
              {/* Not an <a> — avoids nesting an anchor inside an anchor (the FBI Official Portal button below is its own real link) */}
              <div
                role="link"
                tabIndex={0}
                onClick={() => window.open("https://www.edo.cjis.gov/#/do-info", "_blank", "noopener,noreferrer")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.open("https://www.edo.cjis.gov/#/do-info", "_blank", "noopener,noreferrer");
                  }
                }}
                data-testid="path-step-2-fbi"
                className="group block rounded-2xl border-2 border-[#FF8A3D]/60 bg-[#FF8A3D]/5 hover:bg-[#FF8A3D]/10 hover:border-[#FF8A3D] transition-all p-5 sm:p-6 cursor-pointer"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#FF8A3D] flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-[#FF8A3D]/40">
                    2
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-white text-lg mb-1">STEP 2 — FBI Check</div>
                    <p className="text-[#FFB37A] text-xs uppercase tracking-wider font-bold mb-4">
                      How to complete your FBI check — it's easy!
                    </p>
                    <ul className="space-y-3 text-sm mb-4">
                      {[
                        {
                          title: "Click the FBI Official Portal",
                          desc: "Opens the FBI portal",
                          button: "https://www.edo.cjis.gov/#/do-info",
                        },
                        { title: "Scroll to the bottom", desc: "Of the FBI home page" },
                        { title: "Write your email", desc: "And click submit" },
                        { title: "Check your inbox", desc: "FBI sends you an email with next steps" },
                        { title: "Follow the email", desc: "Complete the form and get your reference number" },
                        { title: "Submit result to our app", desc: "Admin will approve and welcome you to our platform!" },
                      ].map((item, idx) => (
                        <li key={idx} className="flex gap-3 items-start">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#22D3A4]/20 text-[#22D3A4] flex items-center justify-center text-[11px] font-bold mt-0.5">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-semibold leading-tight">{item.title}</p>
                            {item.button ? (
                              <a
                                href={item.button}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                data-testid="fbi-official-portal-btn"
                                aria-label="Open FBI Official Portal"
                                className="flex items-center justify-center w-full rounded-lg bg-[#FF8A3D] hover:bg-[#FF7A1F] transition-colors mt-2 mb-1 py-5 px-4 shadow-lg shadow-[#FF8A3D]/30 border-2 border-[#FFB37A]/40"
                              >
                                <span
                                  className="inline-flex items-center gap-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors shadow-md shadow-[#1E40AF]/30"
                                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  FBI Official Portal
                                </span>
                              </a>
                            ) : null}
                            <p className="text-[#A8A8B3] text-xs leading-snug mt-0.5">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            <li className="flex justify-center" aria-hidden="true">
              <ChevronDown className="w-6 h-6 text-[#FF8A3D]" />
            </li>

            <li>
              <button
                type="button"
                data-testid="path-step-3-admin-approval"
                onClick={onGetStarted}
                className="group w-full text-left rounded-2xl border-2 border-[#22D3A4]/60 bg-[#22D3A4]/5 hover:bg-[#22D3A4]/10 hover:border-[#22D3A4] transition-all p-5 sm:p-6 flex gap-4 items-start"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#22D3A4] flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-[#22D3A4]/40">
                  3
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-lg mb-1">STEP 3 — Admin Approval</div>
                  <p className="text-[#A8A8B3] text-sm leading-relaxed mb-3">
                    Our team reviews and approves your FBI report. You receive a verified badge.
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#22D3A4]/60 text-[#5EE3B8] text-xs font-bold uppercase tracking-wider group-hover:bg-[#22D3A4]/15 transition-colors">
                    <FileCheck className="w-3.5 h-3.5" />
                    Check My Verification Status
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </button>
            </li>

            <li className="flex justify-center" aria-hidden="true">
              <ChevronDown className="w-6 h-6 text-[#22D3A4]" />
            </li>

            <li>
              <button
                type="button"
                data-testid="path-step-4-profile"
                onClick={onGetStarted}
                className="group w-full text-left rounded-2xl border-2 border-[#3B82F6]/60 bg-[#3B82F6]/5 hover:bg-[#3B82F6]/10 hover:border-[#3B82F6] transition-all p-5 sm:p-6 flex gap-4 items-start"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#3B82F6] flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-[#3B82F6]/40">
                  4
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-lg mb-1">STEP 4 — Profile</div>
                  <p className="text-[#A8A8B3] text-sm leading-relaxed mb-3">
                    Answer 25 questions for better matching, plus star signs &amp; numerology setup.
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#3B82F6]/60 text-[#7AB0FF] text-xs font-bold uppercase tracking-wider group-hover:bg-[#3B82F6]/15 transition-colors">
                    <Sparkles className="w-3.5 h-3.5" />
                    Build My Profile
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </button>
            </li>

            <li className="flex justify-center" aria-hidden="true">
              <ChevronDown className="w-6 h-6 text-[#3B82F6]" />
            </li>

            <li>
              <button
                type="button"
                data-testid="path-step-5-verified-users"
                onClick={onGetStarted}
                className="group w-full text-left rounded-2xl border-2 border-[#A855F7]/60 bg-[#A855F7]/5 hover:bg-[#A855F7]/10 hover:border-[#A855F7] transition-all p-5 sm:p-6 flex gap-4 items-start"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#A855F7] flex items-center justify-center font-bold text-white text-xl shadow-lg shadow-[#A855F7]/40">
                  5
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-white text-lg mb-1">STEP 5 — Verified Users</div>
                  <p className="text-[#A8A8B3] text-sm leading-relaxed mb-3">
                    Start browsing profiles and chatting with FBI-verified members.
                  </p>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#A855F7]/60 text-[#C99CFF] text-xs font-bold uppercase tracking-wider group-hover:bg-[#A855F7]/15 transition-colors">
                    <MessagesSquare className="w-3.5 h-3.5" />
                    Start Browsing &amp; Chatting
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </button>
            </li>
          </ol>

          <div className="mt-10 flex justify-center">
            <Button
              data-testid="path-bottom-get-started-btn"
              onClick={onGetStarted}
              className="bg-[#FF3B6B] text-white font-semibold rounded-full border-0 hover:bg-[#E8335F] h-13 px-8 text-base shadow-lg shadow-[#FF3B6B]/30"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              Begin My Safe Dating Journey
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>

        {SUCCESS_STORIES.length > 0 && (
          <div className="relative mt-20">
            <StoryCards stories={SUCCESS_STORIES} />
          </div>
        )}
      </section>

      {/* Stats Bar */}
      <section className="relative bg-white border-y border-[#F0E6DD]">
        <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className="text-3xl md:text-4xl font-bold text-[#FF3B6B] mb-1"
                style={{ fontFamily: "'Bodoni Moda', serif" }}
              >
                {stat.value}
              </div>
              <div
                className="text-[#8A8077] text-xs uppercase tracking-[0.2em]"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 px-6 md:px-16 lg:px-24 bg-[#FFF9F5]">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16 text-center">
            <span
              className="text-[#FF3B6B] text-xs uppercase tracking-[0.2em] block mb-3"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              Why Aurora
            </span>
            <h2
              style={{ fontFamily: "'Bodoni Moda', serif" }}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-[#1A1A1A]"
            >
              Dating Without the Doubt
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, i) => (
              <div
                key={feature.title}
                className="group p-8 bg-white border border-[#F0E6DD] rounded-lg hover:border-[#FF3B6B]/30 hover:shadow-lg hover:shadow-[#FF3B6B]/5 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-full bg-[#FFF0F3] flex items-center justify-center mb-5">
                  <feature.icon className="w-5 h-5 text-[#FF3B6B]" />
                </div>
                <h3 className="text-[#1A1A1A] text-lg font-medium mb-3">
                  {feature.title}
                </h3>
                <p className="text-[#6B6B6B] text-sm leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 md:py-28 px-6 md:px-16 lg:px-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-lg shadow-2xl shadow-[#D4C4B0]/30">
                <img
                  src={COUPLE_IMG}
                  alt="Couple silhouette on bench at sunset"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white border border-[#F0E6DD] rounded-lg p-4 flex items-center gap-3 shadow-lg">
                <div className="w-3 h-3 rounded-full bg-[#00CC55] pulse-badge" />
                <span
                  className="text-[#00994D] text-xs tracking-widest uppercase font-medium"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  Both Verified
                </span>
              </div>
            </div>

            <div>
              <span
                className="text-[#FF3B6B] text-xs uppercase tracking-[0.2em] block mb-3"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                How It Works
              </span>
              <h2
                style={{ fontFamily: "'Bodoni Moda', serif" }}
                className="text-3xl sm:text-4xl tracking-tighter text-[#1A1A1A] mb-10"
              >
                Four Steps to Safe Dating
              </h2>

              <div className="space-y-8">
                {steps.map((step) => (
                  <div key={step.num} className="flex gap-5">
                    <div
                      className="text-[#FF3B6B]/20 text-3xl font-bold flex-shrink-0 w-12"
                      style={{ fontFamily: "'JetBrains Mono', monospace" }}
                    >
                      {step.num}
                    </div>
                    <div>
                      <h4 className="text-[#1A1A1A] font-medium mb-1">
                        {step.title}
                      </h4>
                      <p className="text-[#6B6B6B] text-sm leading-relaxed">
                        {step.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 md:py-32 px-6">
        <div className="absolute inset-0">
          <img
            src={BENCH_IMG}
            alt="Couple watching sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1A1A1A]/60" />
        </div>
        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <ShieldCheck className="w-10 h-10 text-[#00FF66] mx-auto mb-6" />
          <h2
            style={{ fontFamily: "'Bodoni Moda', serif" }}
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white mb-4"
          >
            Ready for Your New Beginning?
          </h2>
          <p className="text-white/70 text-base mb-10 max-w-md mx-auto">
            Join thousands of verified singles who have chosen Aurora — safety and authenticity
            over uncertainty.
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={onGetStarted}
            className="bg-[#FF3B6B] text-white font-semibold rounded-full border-0 hover:bg-[#E8335F] h-13 px-10 text-base shadow-lg shadow-[#FF3B6B]/30"
          >
            Get Started Free
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-16 px-6 bg-[#FFF9F5] border-t border-[#F0E6DD]">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[#8A8077] text-sm mb-4" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            FOUNDER & CEO
          </p>
          <p className="text-[#6B6B6B] text-base sm:text-lg leading-relaxed italic max-w-lg mx-auto" style={{ fontFamily: "'Bodoni Moda', serif" }}>
            "Where there is fear, there is no room for love. Fear and love can't live in the same heart.
            That's why I built a place where fear doesn't exist — because every person here is real,
            verified, and ready for something genuine."
          </p>
        </div>
      </section>

      {/* Footer */}
      <AuroraFooter />
    </div>
  );
}